import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Radio, Plus, Loader2, Clock, MapPin, User, CheckCircle2, PlayCircle, Circle, Sparkles, Users, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const eventTypeIcons = {
  ceremony: Sparkles,
  vendor_arrival: Users,
  task: CheckCircle2,
  milestone: Radio
};

const eventTypeColors = {
  ceremony: 'bg-primary/10 text-primary border-primary/20',
  vendor_arrival: 'bg-secondary/10 text-secondary-foreground border-secondary/30',
  task: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  milestone: 'bg-purple-500/10 text-purple-600 border-purple-500/20'
};

export const CommandCenterPage = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [form, setForm] = useState({
    title: '',
    event_type: 'milestone',
    start_time: '',
    end_time: '',
    location: '',
    responsible: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const eventsRes = await axios.get(`${API_URL}/api/timeline`, { withCredentials: true });
      setEvents(eventsRes.data);
      if (user?.wedding_id) {
        const weddingRes = await axios.get(`${API_URL}/api/weddings/${user.wedding_id}`, { withCredentials: true });
        setWedding(weddingRes.data);
      }
    } catch (error) {
      toast.error('Error loading command center');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/timeline`, form, { withCredentials: true });
      toast.success('Event added to timeline!');
      setDialogOpen(false);
      setForm({ title: '', event_type: 'milestone', start_time: '', end_time: '', location: '', responsible: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Error adding event');
    }
  };

  const updateStatus = async (eventId, status) => {
    try {
      await axios.patch(`${API_URL}/api/timeline/${eventId}?status=${status}`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API_URL}/api/timeline/${eventId}`, { withCredentials: true });
      toast.success('Event removed');
      fetchData();
    } catch (error) {
      toast.error('Error deleting event');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const getCountdown = () => {
    if (!wedding?.wedding_date) return null;
    const weddingDate = new Date(wedding.wedding_date);
    const diff = weddingDate - now;
    if (diff < 0) return { days: 0, hours: 0, minutes: 0, past: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { days, hours, minutes, past: false };
  };

  const countdown = getCountdown();

  const upcomingEvents = events.filter(e => e.status === 'upcoming');
  const inProgressEvents = events.filter(e => e.status === 'in_progress');
  const completedEvents = events.filter(e => e.status === 'completed');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Command Center</h1>
          <p className="text-muted-foreground mt-1">Live wedding day timeline & operations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-event-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Timeline Event</DialogTitle>
              <DialogDescription>Add ceremonies, vendor arrivals, tasks, or milestones</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Event Title</Label>
                <Input
                  placeholder="e.g., Photographer arrival"
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  required
                  data-testid="event-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.event_type} onValueChange={(val) => setForm({...form, event_type: val})}>
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ceremony">Ceremony</SelectItem>
                    <SelectItem value="vendor_arrival">Vendor Arrival</SelectItem>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="milestone">Milestone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(e) => setForm({...form, start_time: e.target.value})}
                    required
                    data-testid="event-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="datetime-local"
                    value={form.end_time}
                    onChange={(e) => setForm({...form, end_time: e.target.value})}
                    data-testid="event-end-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  placeholder="Event location"
                  value={form.location}
                  onChange={(e) => setForm({...form, location: e.target.value})}
                  data-testid="event-location-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Responsible Person</Label>
                <Input
                  placeholder="Who is in charge"
                  value={form.responsible}
                  onChange={(e) => setForm({...form, responsible: e.target.value})}
                  data-testid="event-responsible-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional details"
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  data-testid="event-notes-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-event-button">
                Add to Timeline
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {countdown && (
        <Card className="shadow-[0_8px_30px_rgba(139,30,65,0.1)] bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="p-8">
            {countdown.past ? (
              <div className="text-center">
                <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-2">The Day Has Arrived</p>
                <h2 className="text-4xl font-serif font-bold">Wedding Day is Here!</h2>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">Countdown to Wedding</p>
                <div className="flex justify-center items-center gap-4 md:gap-8" data-testid="countdown-display">
                  <div>
                    <div className="text-4xl md:text-5xl font-serif font-bold text-primary">{countdown.days}</div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Days</div>
                  </div>
                  <div className="text-2xl md:text-3xl text-muted-foreground/40">:</div>
                  <div>
                    <div className="text-4xl md:text-5xl font-serif font-bold text-primary">{countdown.hours}</div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Hours</div>
                  </div>
                  <div className="text-2xl md:text-3xl text-muted-foreground/40">:</div>
                  <div>
                    <div className="text-4xl md:text-5xl font-serif font-bold text-primary">{countdown.minutes}</div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Minutes</div>
                  </div>
                </div>
                {wedding?.wedding_date && (
                  <p className="text-sm text-muted-foreground mt-4">
                    {new Date(wedding.wedding_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Upcoming</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground" data-testid="upcoming-count">{upcomingEvents.length}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400" data-testid="inprogress-count">{inProgressEvents.length}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600 dark:text-green-400" data-testid="completed-count">{completedEvents.length}</div>
          </CardContent>
        </Card>
      </div>

      {events.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Radio className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No timeline events yet. Add your first event!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <h2 className="text-lg font-medium uppercase tracking-wider text-muted-foreground mb-4">Timeline</h2>
          {events.map((event) => {
            const Icon = eventTypeIcons[event.event_type] || Radio;
            const eventTime = new Date(event.start_time);
            const isPast = eventTime < now;
            const isNear = !isPast && (eventTime - now) < 60 * 60 * 1000;

            return (
              <Card
                key={event.id}
                className={`shadow-sm transition-colors ${
                  event.status === 'completed' ? 'opacity-60' :
                  isNear ? 'border-primary/40 bg-primary/5' : ''
                }`}
                data-testid="timeline-event"
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg border ${eventTypeColors[event.event_type]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-medium">{event.title}</h3>
                            <span className="text-xs uppercase tracking-wider text-muted-foreground">
                              {event.event_type.replace('_', ' ')}
                            </span>
                            {isNear && event.status === 'upcoming' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-medium">
                                Starting Soon
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {eventTime.toLocaleString()}</span>
                            {event.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {event.location}</span>}
                            {event.responsible && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {event.responsible}</span>}
                          </div>
                          {event.notes && (
                            <p className="text-sm text-muted-foreground mt-2">{event.notes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Select value={event.status} onValueChange={(val) => updateStatus(event.id, val)}>
                            <SelectTrigger className="w-[140px] h-8" data-testid="event-status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="upcoming">Upcoming</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteEvent(event.id)}
                            data-testid="delete-event-button"
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
